<template>
  <div>
    <Head title="API & Webhooks" />
    <h1 class="mb-8 text-3xl font-bold">Webhooks</h1>


    <div class="px-4">
      <ul  class="list-disc list-inside">
        <li class="p-2">A POST request will be sent to the given URL.</li>
        <li class="p-2">The body of the request will be JSON encoded.</li>
        <li class="p-2">The body of the request will contain these types of events: <strong>ORDER_STATUS_CHANGED</strong>, <strong>LIQUIDATION_CREATED</strong>, <strong>LIQUIDATION_STATUS_CHANGED</strong></li>
        <li class="p-2">The request will have a header called Signature that will contain a signature(signed with sha256) the receiving app can use to verify the payload hasn't been tampered with.</li>
        <li class="p-2">When the app to which we're sending the webhook fails to send a response with a 2xx status code we will consider the call as failed. The call will also be considered failed if the remote app doesn't respond within 3 seconds.</li>
        <li class="p-2">If the receiving app doesn't respond with a response code starting with 2xx, we will retry calling the webhook after 10 seconds. If that second attempt fails, we will attempt to call the webhook a final time after 100 seconds.</li>
      </ul>
    </div>


    <div class="flex flex-wrap  space-y-4  lg:space-y-0 mb-4 mt-5">
      <div class=" bg-white rounded-md shadow overflow-hidden mb-4 w-full lg:w-full">
        <form @submit.prevent="updateWebhookFields">
          <div class="flex flex-wrap -mb-8 -mr-6 p-8">
            <text-input v-model="form.webhook_url" :error="form.errors.webhook_url" class="pb-8 pr-6 w-full" label="Webhook URL"/>

            <text-input v-model="form.webhook_secret" :error="form.errors.webhook_secret" class="pb-8 pr-6 w-full" label="Webhook Secret"  info="You will get a header called Signature, which will be the payload of request signed with this secret string."/>
          </div>

          <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
            <loading-button  class="btn-indigo ml-auto" type="submit">Ruaj</loading-button>
          </div>
        </form>
      </div>


      <div class=" bg-white rounded-md shadow overflow-hidden mb-4 w-full lg:w-full">

        <div class="flex flex-wrap  -mr-6 p-8">
          <p  class="pb-8 pr-6 w-full">Request Body Examples</p>

          <pre class="bg-green-200">
{
  "event_type": "<strong>ORDER_STATUS_CHANGED</strong>",
  "order_id": "ID123456",
  "custom_order_id": "000000",
  "old_status": {
    "id": 2,
    "name": "Në proces"
  },
  "new_status": {
    "id": 3,
    "name": "Në depo"
  },
  "description": "Dërgesa ka arritur në depo.",
  "comment": "",
  "timestamp": 1691834148
}

          </pre>

          <pre class="bg-green-200">
{
  "event_type": "<strong>LIQUIDATION_CREATED</strong>",
  "liquidation_id": 19,
  "total_sales": "403.00",
  "total_neto_amount": "395.00",
  "orders": [
    {
      "order_id": "ID111111",
      "custom_order_id": "000000"
    },
    {
      "order_id": "ID222222",
      "custom_order_id": "000000"
    }
  ],
  "timestamp": 1706295990
}
          </pre>


          <pre class="bg-green-200">
{
  "event_type": "<strong>LIQUIDATION_STATUS_CHANGED</strong>",
  "liquidation_id": 19,
  "old_status": {
    "id": 1,
    "name": "E regjistruar"
  },
  "new_status": {
    "id": 2,
    "name": "Në depo"
  },
  "total_sales": "403.00",
  "total_neto_amount": "395.00",
  "orders": [
    {
      "order_id": "ID111111",
      "custom_order_id": "000000"
    },
    {
      "order_id": "ID222222",
      "custom_order_id": "000000"
    }
  ],
  "description": "Barazimi është pranuar nga shitësi.",
  "comment": "",
  "timestamp": 1706295990
}

          </pre>

        </div>
      </div>
    </div>

    <div class="flex items-center mb-6">
      <h2 class="mt-12 text-2xl font-bold">Webhook Events
        <Link class="btn-indigo bg-green-700 mr-3" @click.prevent="resetEvents">
          <span>Refresh</span>
        </Link>


        <Link class="btn-indigo bg-blue-300 mr-3" @click.prevent="sendTestWebhook">
          <span>Send test webhook</span>
        </Link>

      </h2>

    </div>
    <div  class="bg-white rounded-md shadow overflow-x-auto">

      <table class="w-full whitespace-nowrap">
        <thead>
        <tr class="text-left font-bold">
          <th class="pb-4 pt-6 px-6">Status</th>
          <th class="pb-3 pt-6 px-6">ID</th>
          <th class="pb-3 pt-6 px-6">Event Type</th>
          <th class="pb-3 pt-6 px-6">Attempts</th>
          <th class="pb-3 pt-6 px-6">Response</th>
          <th class="pb-3 pt-6 px-6">Created at</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="event in events.data" class="hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-6"  tabindex="-1">
              <icon name="dot" class="block w-10 h-10" v-bind:class="getStatusColor(event.status)"></icon>
              {{ event.status }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" >
              {{ event.uuid }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" >
              {{ event.payload ? event.payload.event_type : '' }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" >
              {{ event.attempt }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" >
              {{ event.response.status_code }}  {{event.response.reason_phrase}}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-6 py-4"  tabindex="-1">
              {{ event.created_at }}
            </Link>
          </td>

        </tr>
        <tr v-if="events.length === 0">
          <td class="px-6 py-4 border-t" colspan="4">Nuk u gjet asnjë event.</td>
        </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'
import LoadingButton from '@/Shared/LoadingButton'
import TextInput from '@/Shared/TextInput'
import {Head, Link} from '@inertiajs/inertia-vue3'
import axios from 'axios'
import {Inertia} from '@inertiajs/inertia'



export default {
  components: {
    Head,
    Link,
    Icon,
    LoadingButton,
    TextInput
  },
  layout: Layout,
  props:{
    webhook_url: String,
    webhook_secret: String,
    events: Object
  },
  data(){
    return{
      form: this.$inertia.form({
        webhook_url: this.webhook_url,
        webhook_secret: this.webhook_secret
      })
    }
  },
  methods: {
    updateWebhookFields(){
      this.form.put('/webhooks')
    },
    resetEvents(){
      Inertia.reload({
        only: ['events']
      });
    },
    sendTestWebhook(){
      let _this = this
      axios.post('/webhooks/send/test/event').then(function (){
        setTimeout(function (){
          _this.resetEvents()
        },3000)
      })
    },
    getStatusColor(status){
      let statusColor = 'fill-orange-400';
      if(status === 'failed')
        statusColor = 'fill-red-600';

      if(status === 'success')
        statusColor = 'fill-green-600'

      return statusColor;
    },


    destroyToken(tokenId){
      if (confirm('Kujdes! Ky token nuk mund të rikthehet më. A jeni të sigurt se dëshironi të vazhdoni?')) {
        this.$inertia.delete(`/api/tokens/${tokenId}`)
      }
    },
    generateNewToken(){
      self = this;

      axios.post(route('api.new.token'),{
          token_name: this.token_name,
      }).then(function (result){
        if(result.data.errors && result.data.errors.token_name){
          self.token_name_error =  result.data.errors.token_name[0];
          self.newToken = null
        }
        else{
          self.token_name_error = null;
          self.newToken = result.data.newToken
          Inertia.reload();
        }
      })
    }
  }
}
</script>
